import React, { useState } from 'react';
import OrderDesign from './OrderDesign';
import { useTranslation } from 'react-i18next';
import { playSoundEffect } from './hooks/useSoundEffects';
import CustomDropdown from './CustomDropdown';

// Helper function to get rarity sort order
const getRarityOrder = (rarity) => {
  const rarityOrder = { 'common': 1, 'rare': 2, 'epic': 3, 'legendary': 4 };
  return rarityOrder[(rarity || 'common').toLowerCase()] || 0;
};

// Faction colors for strategy display
const factionColors = {
  'Remnants of Humanity': '#556B2F',
  'Exolot Dominion': '#4B0082',
  'League of Shadows': '#1A1A1A',
  'Ra\'s Children': '#F4A460',
  'Asrah': '#8B0000',
  'The Sentients': '#ADD8E6',
  'Combined': '#FFA500'
};

const combinedFactions = {
  'The Conspiracy': ['The Conspiracy', 'League of Shadows', 'Asrah', 'The Sentients'],
  'The Corrupted Core': ['The Corrupted Core', 'Exolot Dominion', 'Asrah', 'The Sentients'],
  'The Covenant': ['The Covenant', 'Ra\'s Children', 'League of Shadows', 'The Sentients'],
  'The Divine Syndicate': ['The Divine Syndicate', 'Ra\'s Children', 'Exolot Dominion', 'The Sentients'],
  'The Forsaken Pact': ['The Forsaken Pact', 'Remnants of Humanity', 'Asrah', 'The Sentients'],
  'The Hollow': ['The Hollow', 'Ra\'s Children', 'Asrah', 'The Sentients'],
  'The Iron Alliance': ['The Iron Alliance', 'Remnants of Humanity', 'Exolot Dominion', 'The Sentients'],
  'The Last Crusade': ['The Last Crusade', 'Ra\'s Children', 'Remnants of Humanity', 'The Sentients'],
  'The Shadow Consortium': ['The Shadow Consortium', 'League of Shadows', 'Exolot Dominion', 'The Sentients'],
  'The Silent Accord': ['The Silent Accord', 'Remnants of Humanity', 'League of Shadows', 'The Sentients']
};

function StrategyModal({ show, onClose, strategy, gameState, userId = null, hoveredOrderIndex, onHoverOrder, playerState, commandDirectiveActive = false, onCommandDirectiveSelect = null }) {
  const { t } = useTranslation();
  const [hoveredOrderId, setHoveredOrderId] = useState(null);
  const [sortBy, setSortBy] = useState('cost'); // 'cost', 'rarity', 'name', 'type'

  if (!show || !strategy?.orders) return null;

  // Expand orders based on count and track which ones are drawn
  const expandedOrders = [];
  const drawnCounts = new Map(); // Track how many of each order ID have been drawn
  
  // First, count how many of each order have been drawn
  if (playerState) {
    const allDrawnOrders = [
      ...(playerState.hand || []),
      ...(playerState.discard || []),
      ...(playerState.playedOrders?.front || []),
      ...(playerState.playedOrders?.flank || []),
      ...(playerState.completedOrders || [])
    ];
    
    allDrawnOrders.forEach(order => {
      if (order?.id) {
        drawnCounts.set(order.id, (drawnCounts.get(order.id) || 0) + 1);
      }
    });
  }

  strategy.orders.forEach(order => {
    const count = order.count || 1;
    const drawnCount = drawnCounts.get(order.id) || 0;
    
    for (let i = 0; i < count; i++) {
      expandedOrders.push({ 
        ...order,
        isDrawn: i < drawnCount // Mark first N copies as drawn
      });
    }
  });
  
  // Sort by selected option
  expandedOrders.sort((a, b) => {
    // Primary sort by selected option
    let primaryComparison = 0;
    switch (sortBy) {
      case 'cost':
        primaryComparison = (a.cost ?? 0) - (b.cost ?? 0);
        break;
      case 'rarity':
        primaryComparison = getRarityOrder(a.rarity) - getRarityOrder(b.rarity);
        break;
      case 'name':
        primaryComparison = (a.name || '').localeCompare(b.name || '');
        break;
      case 'type':
        primaryComparison = (a.type || '').localeCompare(b.type || '');
        break;
      default:
        primaryComparison = (a.cost ?? 0) - (b.cost ?? 0);
    }

    if (primaryComparison !== 0) return primaryComparison;

    // Secondary sort by name (except when already sorting by name)
    if (sortBy !== 'name') {
      return (a.name || '').localeCompare(b.name || '');
    }

    // If sorting by name and names are equal, sort by cost
    return (a.cost ?? 0) - (b.cost ?? 0);
  });
  
  const hoveredOrder = hoveredOrderIndex !== null ? expandedOrders[hoveredOrderIndex] : null;

  return (
    <div 
      className="modal-overlay"
      style={{
        position: 'absolute',
        top: '-100%',
        left: '-100%',
        width: '300%',
        height: '300%',
        background: 'rgba(0,0,0,0.7)',
        zIndex: 10001,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      {/* Expanded OrderDesign shown to the right of the modal */}
      {hoveredOrder && (
        <div
          style={{
            position: 'absolute',
            left: 'calc(50% + 300px)',
            top: '50%',
            transform: 'translateY(-50%)',
            marginLeft: '20px',
            zIndex: 10002, // Higher than modal overlay to ensure expanded order appears on top
            pointerEvents: 'none'
          }}
        >
          <OrderDesign
            order={hoveredOrder}
            count={0}
            minimal={false}
            lazyLoad={true}
            gameState={gameState}
            userId={userId}
          />
        </div>
      )}
      <div 
        style={{
          background: '#121212',
          padding: '24px',
          borderRadius: '12px',
          minWidth: '400px',
          maxWidth: '600px',
          maxHeight: '864px', // 80% of 1080px base height
          overflowY: 'auto',
          overflowX: 'visible',
          boxShadow: '0 0 24px #000',
          position: 'relative'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '20px',
          borderBottom: '1px solid #333',
          paddingBottom: '10px'
        }}>
          <h3 style={{ color: 'white', margin: 0 }}>
            {t('battleUI.strategy') || 'Strategy'}
          </h3>
          <button
            onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              onClose();
            }}
            style={{
              background: '#333',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '8px 16px',
              cursor: 'pointer',
              fontSize: '1em',
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.background = '#444'}
            onMouseLeave={(e) => e.target.style.background = '#333'}
          >
            {t('battleUI.close') || 'Close'}
          </button>
        </div>
        
        <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ color: '#aaa', fontSize: '0.9em' }}>
            {t('battleUI.totalStrategyOrders') || 'Total'}: {expandedOrders.length}
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#aaa', fontSize: '0.9em', whiteSpace: 'nowrap' }}>
            {t('strategy.sortBy') || 'Sort by:'}
            <div style={{ minWidth: '150px', maxWidth: '150px' }}>
              <CustomDropdown
                options={[
                  { value: 'cost', label: t('strategy.sort.cost') || 'Cost' },
                  { value: 'rarity', label: t('strategy.sort.rarity') || 'Rarity' },
                  { value: 'name', label: t('strategy.sort.name') || 'Name' },
                  { value: 'type', label: t('strategy.sort.type') || 'Type' }
                ]}
                value={sortBy}
                onChange={(value) => {
                  playSoundEffect('/sounds/click.mp3');
                  setSortBy(value);
                }}
                placeholder={t('strategy.sortBy') || 'Sort by:'}
              />
            </div>
          </label>
        </div>

        <div style={{ 
          maxHeight: '648px', // 60% of 1080px base height
          overflowY: 'auto',
          scrollbarColor: '#333 #121212',
          scrollbarWidth: 'thin',
          position: 'relative'
        }}>
          {expandedOrders.map((order, index) => {
            const color = order
              ? (combinedFactions[order.faction_name]
                  ? factionColors['Combined']
                  : factionColors[order.faction_name] || '#FFFFFF')
              : '#FFFFFF';

            const isDrawn = order.isDrawn || false;
            const isHovered = hoveredOrderId === order.id;

            return (
              <div
                key={`strategy_${order.id || index}_${index}`}
                onMouseEnter={() => {
                  playSoundEffect('/sounds/hoverover.mp3', 0.3);
                  onHoverOrder(index);
                  setHoveredOrderId(order.id);
                }}
                onMouseLeave={() => {
                  onHoverOrder(null);
                  setHoveredOrderId(null);
                }}
                style={{
                  border: '0px solid #ccc',
                  padding: '5px',
                  marginBottom: '5px',
                  borderRadius: '4px',
                  backgroundColor: color,
                  fontSize: '0.9em',
                  cursor: 'default',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  opacity: isDrawn ? 0.4 : 1,
                  filter: isDrawn ? 'grayscale(0.6)' : 'none',
                  transition: 'opacity 0.2s ease, filter 0.2s ease',
                  position: 'relative'
                }}
              >
                <div>
                  <strong>{order.name || 'Unknown Order'}</strong>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span>{order.cost ?? 'N/A'}</span>
                  {commandDirectiveActive && !isDrawn && isHovered && onCommandDirectiveSelect && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onCommandDirectiveSelect(order.id);
                      }}
                      style={{
                        background: '#FFD700',
                        color: '#000',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '4px 12px',
                        cursor: 'pointer',
                        fontSize: '0.85em',
                        fontWeight: '600',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#FFA500';
                        e.target.style.transform = 'scale(1.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = '#FFD700';
                        e.target.style.transform = 'scale(1)';
                      }}
                    >
                      {t('battleUI.get') || 'Get'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default StrategyModal;

